/**
 * usePlayerMessages - Aggregates character messages across all visited stages
 * Tracks read state and timestamps in sessionStorage
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { contentLoader } from '@/game/content';
import { STAGES, StageId } from '@/game/models/GameState';
import { PlayerRole } from '@/game/models/Player';

export interface PlayerMessage {
  id: string;
  stage: StageId;
  stageTitle: string;
  from: string;
  icon: string;
  text: string;
  timestamp: number;
  isRead: boolean;
}

interface UsePlayerMessagesReturn {
  messages: PlayerMessage[];
  unreadCount: number;
  newMessages: PlayerMessage[];
  markAllRead: () => void;
  markRead: (messageId: string) => void;
  clearNewMessages: () => void;
}

function getTimestampKey(sessionId: string) {
  return `cesim_msg_timestamps_${sessionId}`;
}

function getReadKey(sessionId: string) {
  return `cesim_msg_read_${sessionId}`;
}

function loadTimestamps(sessionId: string): Record<number, number> {
  try {
    const raw = sessionStorage.getItem(getTimestampKey(sessionId));
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function saveTimestamps(sessionId: string, timestamps: Record<number, number>) {
  sessionStorage.setItem(getTimestampKey(sessionId), JSON.stringify(timestamps));
}

function loadReadSet(sessionId: string): Set<string> {
  try {
    const raw = sessionStorage.getItem(getReadKey(sessionId));
    return raw ? new Set(JSON.parse(raw)) : new Set();
  } catch {
    return new Set();
  }
}

function saveReadSet(sessionId: string, readSet: Set<string>) {
  sessionStorage.setItem(getReadKey(sessionId), JSON.stringify([...readSet]));
}

export function usePlayerMessages(
  currentStage: StageId | null,
  role: PlayerRole | null,
  sessionId: string | undefined
): UsePlayerMessagesReturn {
  const [readSet, setReadSet] = useState<Set<string>>(new Set());
  const [newMessageIds, setNewMessageIds] = useState<Set<string>>(new Set());
  const prevStageRef = useRef<StageId | null>(null);

  // Initialize read set from sessionStorage
  useEffect(() => {
    if (sessionId) {
      setReadSet(loadReadSet(sessionId));
    }
  }, [sessionId]);

  // Build all messages from stages 0 through currentStage
  const messages = useMemo(() => {
    if (currentStage === null || !role || !sessionId) return [];

    const timestamps = loadTimestamps(sessionId);
    const allMessages: PlayerMessage[] = [];

    for (let stage = 0; stage <= currentStage; stage++) {
      const stageId = stage as StageId;
      const stageInfo = STAGES[stageId];
      if (!stageInfo) continue;

      // Record timestamp for this stage if not yet seen
      if (timestamps[stage] === undefined) {
        timestamps[stage] = Date.now();
        saveTimestamps(sessionId, timestamps);
      }

      const stageMessages = contentLoader.getStageCharacterMessages(stageId, role);
      stageMessages.forEach((msg, index) => {
        const id = `s${stage}-m${index}`;
        allMessages.push({
          id,
          stage: stageId,
          stageTitle: stageInfo.name,
          from: contentLoader.getMessageSource(msg.icon),
          icon: msg.icon,
          text: msg.message,
          timestamp: timestamps[stage],
          isRead: readSet.has(id),
        });
      });
    }

    return allMessages;
  }, [currentStage, role, sessionId, readSet]);

  // Detect stage changes and populate newMessages
  useEffect(() => {
    if (currentStage === null || !role || !sessionId) return;

    // Skip first render (prevStageRef starts null)
    if (prevStageRef.current === null) {
      prevStageRef.current = currentStage;
      return;
    }

    if (prevStageRef.current !== currentStage) {
      // Stage changed — find messages from the new stage that are unread
      const newIds = new Set<string>();
      messages.forEach(msg => {
        if (msg.stage === currentStage && !msg.isRead) {
          newIds.add(msg.id);
        }
      });
      setNewMessageIds(newIds);
      prevStageRef.current = currentStage;
    }
  }, [currentStage, role, sessionId, messages]);

  const unreadCount = useMemo(
    () => messages.filter(m => !m.isRead).length,
    [messages]
  );

  const newMessages = useMemo(
    () => messages.filter(m => newMessageIds.has(m.id)),
    [messages, newMessageIds]
  );

  const markAllRead = useCallback(() => {
    if (!sessionId) return;
    const updated = new Set(readSet);
    messages.forEach(m => updated.add(m.id));
    setReadSet(updated);
    saveReadSet(sessionId, updated);
    setNewMessageIds(new Set());
  }, [sessionId, readSet, messages]);

  const markRead = useCallback((messageId: string) => {
    if (!sessionId) return;
    const updated = new Set(readSet);
    updated.add(messageId);
    setReadSet(updated);
    saveReadSet(sessionId, updated);
  }, [sessionId, readSet]);

  const clearNewMessages = useCallback(() => {
    setNewMessageIds(new Set());
  }, []);

  return { messages, unreadCount, newMessages, markAllRead, markRead, clearNewMessages };
}
